<template>
  <div class="min-h-screen bg-slate-50 text-slate-800 font-sans flex flex-col">
    


    <!-- Main Content Area -->
    <main class="flex-1 p-6 sm:p-8 max-w-5xl mx-auto w-full">
      
      <!-- Navigation par Onglets (Tabs) -->
      <div class="flex border-b border-slate-200 mb-8 gap-8">
        <button 
          @click="selectTab('hours')" 
          :class="activeTab === 'hours' ? 'border-sky-600 text-sky-700 font-bold' : 'border-transparent text-slate-500 hover:text-slate-800 font-medium'"
          class="pb-4 border-b-2 text-sm transition-all flex items-center gap-2"
        >
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Heures d'Ouverture
        </button>
        <button 
          @click="selectTab('closedDates')" 
          :class="activeTab === 'closedDates' ? 'border-sky-600 text-sky-700 font-bold' : 'border-transparent text-slate-500 hover:text-slate-800 font-medium'"
          class="pb-4 border-b-2 text-sm transition-all flex items-center gap-2"
        >
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Dates Fermées & Congés
        </button>
      </div>

      <!-- ONGLET 1 : Heures d'Ouverture -->
      <div v-if="activeTab === 'hours'" class="space-y-6">
        <div class="bg-white p-6 sm:p-8 rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50">
          <div class="mb-6">
            <h3 class="text-lg font-bold text-slate-900 mb-1">Configuration des horaires</h3>
            <p class="text-xs text-slate-500">Sélectionnez les jours actifs de la semaine et définissez vos plages horaires.</p>
          </div>

          <!-- Boutons de sélection des jours -->
          <div class="flex flex-wrap justify-center gap-3 mb-8 bg-slate-50 p-4 rounded-xl border border-slate-100">
            <button 
              v-for="day in ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']"
              :key="day"
              :class="selectedDays.includes(day) ? 'bg-sky-700 text-white shadow-md shadow-sky-700/20' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'" 
              @click="toggleDay(day)" 
              type="button" 
              class="w-11 h-11 rounded-xl text-sm font-bold transition-all flex items-center justify-center"
            >
              {{ day.charAt(0) }}
            </button>
          </div>

          <!-- Champ de durée des créneaux & Enregistrement -->
          <div class="bg-slate-50 p-5 rounded-xl border border-slate-100 mb-8 flex flex-col sm:flex-row items-center gap-4 justify-between">
            <div class="w-full sm:w-auto flex-1">
              <label for="timeSlotDuration" class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Durée des créneaux (en minutes)
              </label>
              <input 
                id="timeSlotDuration"
                type="number" 
                v-model="timeSlotDuration" 
                placeholder="Ex: 30" 
                class="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 transition-all"
                required 
              />
            </div>
            <div class="w-full sm:w-auto sm:self-end">
              <button 
                class="w-full sm:w-auto rounded-xl bg-sky-700 text-white px-6 py-2.5 font-semibold text-sm shadow-md shadow-sky-700/20 hover:bg-sky-800 transition-all"
                @click="saveOpenDays" 
              >
                Enregistrer les paramètres
              </button>
            </div>
          </div>

          <!-- Liste des créneaux par jour sélectionné -->
          <div class="space-y-4">
            <div v-if="selectedDays.length === 0" class="text-center py-8 text-slate-400 italic text-sm">
              Aucun jour sélectionné. Cliquez sur les lettres ci-dessus pour activer des jours.
            </div>

            <div v-for="day in selectedDays" :key="day" class="flex flex-col sm:flex-row sm:items-center justify-between border border-slate-100 bg-white p-4 rounded-xl shadow-sm gap-4">
              <span class="font-bold text-slate-800 text-sm w-28">{{ day }}</span>
              
              <!-- Créneaux horaires -->
              <div class="flex flex-col flex-1 space-y-2">
                <div v-for="(slot, index) in (openingTimes[day]?.slots || [])" :key="index" class="flex items-center gap-3">
                  <input class="border border-slate-200 rounded-lg p-2 text-sm text-slate-700 bg-slate-50 w-36 focus:outline-none focus:border-sky-500" type="time" v-model="slot.start" />
                  <span class="text-slate-400 font-medium">à</span>
                  <input class="border border-slate-200 rounded-lg p-2 text-sm text-slate-700 bg-slate-50 w-36 focus:outline-none focus:border-sky-500" type="time" v-model="slot.end" />
                  <button @click="removeSlot(day, index)" class="text-rose-500 hover:text-rose-700 p-2 transition-colors" title="Supprimer ce créneau">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
              
              <div>
                <button @click="addSlot(day)" class="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 bg-sky-50 hover:bg-sky-100 px-3 py-2 rounded-lg transition-colors">
                  <i class="fas fa-plus"></i> Ajouter un créneau
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- ONGLET 2 : Dates Fermées -->
      <div v-if="activeTab === 'closedDates'" class="space-y-6">
        
        <!-- Formulaire d'ajout -->
        <form @submit.prevent="addClosedDate" class="max-w-xl mx-auto p-6 sm:p-8 bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50">
          <h3 class="text-lg font-bold mb-1 text-slate-900 text-center">Ajouter une Période de Fermeture</h3>
          <p class="text-xs text-slate-500 text-center mb-6">Bloquez une date unique ou une plage complète pour vos congés.</p>
          
          <div class="flex flex-col space-y-4 mb-6">
            <div v-if="isSingleDate" class="flex flex-col">
              <label class="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">Date Unique :</label>
              <input
                v-model="newDate"
                type="date"
                class="border border-slate-200 rounded-xl p-3 text-sm text-slate-800 bg-slate-50 focus:outline-none focus:border-sky-500"
              />
            </div>

            <div v-if="!isSingleDate" class="flex flex-col space-y-4">
              <div class="flex flex-col">
                <label class="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">Date de Début :</label>
                <input
                  v-model="startDate"
                  type="date"
                  class="border border-slate-200 rounded-xl p-3 text-sm text-slate-800 bg-slate-50 focus:outline-none focus:border-sky-500"
                />
              </div>

              <div class="flex flex-col">
                <label class="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">Date de Fin :</label>
                <input
                  v-model="endDate"
                  type="date"
                  class="border border-slate-200 rounded-xl p-3 text-sm text-slate-800 bg-slate-50 focus:outline-none focus:border-sky-500"
                />
              </div>
            </div>

            <button
              type="submit"
              class="w-full bg-sky-700 text-white px-4 py-3 rounded-xl font-semibold text-sm hover:bg-sky-800 shadow-md shadow-sky-700/20 transition-all mt-2"
            >
              Ajouter la fermeture
            </button>
          </div>

          <!-- Bascule Date unique / Plage -->
          <div class="flex justify-center border-t border-slate-100 pt-4">
            <button @click.prevent="toggleDateType" class="text-xs font-semibold text-sky-700 hover:text-sky-800 transition-colors">
              {{ isSingleDate ? 'Ou basculer vers une plage de dates' : 'Ou basculer vers une date unique' }}
            </button>
          </div>
        </form>

        <!-- Liste des dates fermées -->
        <div class="max-w-xl mx-auto">
          <h4 class="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Historique des fermetures enregistrées</h4>
          
          <ul v-if="closedDates.length" class="space-y-3">
            <li v-for="entry in closedDates" :key="entry._id" class="border border-slate-100 rounded-2xl p-4 shadow-sm bg-white">
              <ul class="space-y-2">
                <li v-for="dateEntry in entry.dates" :key="dateEntry._id" class="flex justify-between items-center text-sm">
                  <span class="text-slate-700 font-medium">
                    <template v-if="dateEntry.startDate && dateEntry.endDate">
                      <span class="text-sky-700 font-semibold">Plage :</span> {{ formatDate(dateEntry.startDate) }} au {{ formatDate(dateEntry.endDate) }} 
                    </template>
                    <template v-else>
                      <span class="text-sky-700 font-semibold">Date unique :</span> {{ formatDate(dateEntry.date) }}
                    </template>
                  </span>
                  <button @click="deleteClosedDate(dateEntry._id)" class="text-rose-500 hover:text-rose-700 text-xs font-semibold bg-rose-50 px-3 py-1.5 rounded-lg transition-colors">
                    Supprimer
                  </button>
                </li>
              </ul>
            </li>
          </ul>

          <div v-else class="text-center py-8 bg-white border border-slate-100 rounded-2xl text-slate-400 text-sm italic">
            Aucune date fermée programmée.
          </div>
        </div>

      </div>

    </main>
  </div>
</template>
<script>
import axios from 'axios';

export default {
  data() {
    return {
      currentTime: new Date().toLocaleTimeString(),
      timeSlotDuration: 30,
      mode: '',
      seasonMode: 'true', // Ajout d'une propriété pour le mode de saison
      selectedDays: [],
      openingTimes: {
        Lundi: { start: '', end: '' },
        Mardi: { start: '', end: '' },
        Mercredi: { start: '', end: '' },
        Jeudi: { start: '', end: '' },
        Vendredi: { start: '', end: '' },
        Samedi: { start: '', end: '' },
        Dimanche: { start: '', end: '' },
      },
      newClosedDate: '',
      startDate: '',
      endDate: '',
      closedDates: [],
      activeTab: 'hours',
      newDate: '', 

    };
  },
  methods: {
    addSlot(day) {
      // Ajouter un créneau horaire par défaut pour un jour sélectionné
      this.openingTimes[day].slots.push({ start: '', end: '' });
    },
    removeSlot(day, index) {
      // Supprimer un créneau horaire selon l'index
      this.openingTimes[day].slots.splice(index, 1);
    },
    async deleteClosedDate(dateId) {
    const confirmDelete = confirm("Êtes-vous sûr de vouloir supprimer cette date fermée ?");
    if (!confirmDelete) return;

    try {
        // Trouver le groupe de dates auquel appartient la date à supprimer
        const group = this.closedDates.find(entry => 
            entry.dates.some(dateEntry => dateEntry._id === dateId)
        );

        if (group) {
            // Trouver l'index de la date à supprimer dans le groupe
            const dateIndex = group.dates.findIndex(dateEntry => dateEntry._id === dateId);

            // Vérifier si c'est la première date du groupe
            if (dateIndex === 0) {
                // Effectuer la requête de suppression
                const response = await axios.delete(`https://thierry-paradis-medecin-743b8e1c5de0.herokuapp.com/admin/close-dates/${group._id}`); // Utiliser group._id pour la suppression
                console.log(response.data.message); // Afficher le message de succès dans la console

                // Supprimer la date du tableau localement
                group.dates.splice(dateIndex, 1);

                // Récupérer l'ID du groupe
                const groupId = group._id; // ID du groupe de dates
                console.log("ID du groupe :", groupId); // Afficher l'ID du groupe dans la console

                // Si le groupe devient vide après la suppression, le supprimer aussi
                if (group.dates.length === 0) {
                    this.closedDates = this.closedDates.filter(entry => entry._id !== groupId);
                }
            } else {
                alert("Vous ne pouvez supprimer que la première date de ce groupe.");
            }
        } else {
            alert("Date non trouvée dans les groupes de dates fermées.");
        }

        // Recharger les dates fermées après la suppression (si nécessaire)
        this.fetchClosedDates();
    } catch (error) {
        console.error('Erreur lors de la suppression de la date fermée:', error);
        alert('Une erreur est survenue lors de la suppression de la date.');
    }
}
,
toggleDateType() {
      this.isSingleDate = !this.isSingleDate; // Inverser l'état
      this.resetDates(); // Réinitialiser les champs de dates
    },
    resetDates() {
      this.newDate = '';
      this.startDate = '';
      this.endDate = '';
    },
    async addClosedDate() {
      const closedDates = this.isSingleDate
        ? [{ date: this.newDate }] // Si c'est une date unique
        : [{ startDate: this.startDate, endDate: this.endDate }]; // Si c'est une plage de dates

      try {
        const response = await axios.post('https://thierry-paradis-medecin-743b8e1c5de0.herokuapp.com/admin/close-dates', {
          closedDates,
        });
        console.log(response.data.message); // Afficher le message de succès
        this.resetDates(); // Réinitialiser les champs après ajout
        // Recharger les dates fermées si nécessaire
        this.fetchClosedDates();
      } catch (error) {
        console.error('Erreur lors de l\'ajout des dates fermées:', error);
        alert('Une erreur est survenue lors de l\'ajout des dates.');
      }
    },
   
    
    async fetchClosedDates() {
      try {
       const response = await axios.get('https://thierry-paradis-medecin-743b8e1c5de0.herokuapp.com/admin/close-dates'); // Effectuer la requête GET
    // const response = await axios.get('http://localhost:3000/admin/close-dates'); 
        console.log(response.data); // Vérifier la structure des données
        this.closedDates = response.data; // Stocker les dates fermées dans le tableau
      } catch (error) {
        console.error('Erreur lors de la récupération des dates fermées:', error);
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      if (isNaN(date)) {
        console.error('Date invalide:', dateString); // Log pour les dates invalides
        return 'Date invalide'; // Retourner un message d'erreur
      }
      const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
      return date.toLocaleDateString(undefined, options); // Formater la date
    },








    removeClosedDate(id) {
      axios.delete(`https://thierry-paradis-medecin-743b8e1c5de0.herokuapp.com/admin/close-dates/${id}`)
        .then(response => {
          alert(response.data.message || 'Date fermée supprimée avec succès.');
          this.fetchClosedDates(); // Recharger les dates fermées pour mettre à jour l'affichage
        })
        .catch(error => {
          console.error('Erreur lors de la suppression de la date fermée:', error);
          alert('Erreur lors de la suppression de la date fermée');
        });
    },

    updateClosedDate(id, updatedData) {
      axios.put(`https://thierry-paradis-medecin-743b8e1c5de0.herokuapp.com/admin/close-dates/${id}`, updatedData)
        .then(response => {
          alert(response.data.message || 'Date fermée modifiée avec succès.');
          this.fetchClosedDates(); // Recharger les dates fermées pour mettre à jour l'affichage
        })
        .catch(error => {
          console.error('Erreur lors de la modification de la date fermée:', error);
          alert('Erreur lors de la modification de la date fermée');
        });
    },

    fetchOpenDays() {
    axios.get('https://thierry-paradis-medecin-743b8e1c5de0.herokuapp.com/admin/open-days')
   // axios.get('http://localhost:3000/admin/open-days')
        .then(response => {
            const data = response.data;
            console.log(data);
            // Mise à jour du mode de saison
            this.mode = data.seasonMode || 'winter'; // Défaut à 'winter' si non spécifié
            this.timeSlotDuration = data.timeSlotDuration;

            // Mise à jour des jours sélectionnés
            this.selectedDays = data.daysOpen.map(day => this.convertDayToFrench(day));

            // Initialiser les heures d'ouverture et de fermeture
            this.openingTimes = {
                Lundi: { slots: [] },
                Mardi: { slots: [] },
                Mercredi: { slots: [] },
                Jeudi: { slots: [] },
                Vendredi: { slots: [] },
                Samedi: { slots: [] },
                Dimanche: { slots: [] },
            };

            // Récupération des horaires
            for (const day in data.hours) {
                const frenchDay = this.convertDayToFrench(day);
                if (this.selectedDays.includes(frenchDay)) {
                    this.openingTimes[frenchDay].slots = data.hours[day].map(hour => ({
                        start: hour.start,
                        end: hour.end,
                    }));
                }
            }
        })
        .catch(error => {
            console.error('Erreur lors de la récupération des jours ouverts:', error);
            alert('Erreur lors de la récupération des jours ouverts');
        });
},


    convertDayToFrench(day) {
      const daysTranslation = {
        Monday: 'Lundi',
        Tuesday: 'Mardi',
        Wednesday: 'Mercredi',
        Thursday: 'Jeudi',
        Friday: 'Vendredi',
        Saturday: 'Samedi',
        Sunday: 'Dimanche',
      };
      return daysTranslation[day] || day; // Retourner le jour en français ou le jour d'origine
    },

    toggleSidebar() {
      this.$emit('toggleSidebar');
    },

    selectMode(selectedMode) {
      this.mode = selectedMode;
    },

    selectTab(tab) {
      this.activeTab = tab;
    },

    toggleDay(day) {
    if (this.selectedDays.includes(day)) {
      // Si le jour est déjà sélectionné, on le désélectionne et remet les heures à zéro
      this.selectedDays = this.selectedDays.filter(d => d !== day);
      this.openingTimes[day].start = '00:00';  // Heure de début à zéro
      this.openingTimes[day].end = '00:00';    // Heure de fin à zéro
    } else {
      // Sinon, on sélectionne le jour
      this.selectedDays.push(day);
    }
  },

    updateOpenDays() {
      // Logique pour mettre à jour les jours d'ouverture
      console.log(this.selectedDays, this.openingTimes);
    },

   

    addDateRange() {
      if (this.startDate && this.endDate) {
        this.closedDates.push({ startDate: this.startDate, endDate: this.endDate });
        this.startDate = '';
        this.endDate = '';
      }
    },
    
   
    saveOpenDays() {
  const hours = this.selectedDays.reduce((acc, day) => {
    acc[day] = this.openingTimes[day].slots.map(slot => ({
      start: slot.start,
      end: slot.end
    }));
    return acc;
  }, {});

  const payload = {
    daysOpen: this.selectedDays,
    timeSlotDuration: this.timeSlotDuration,
    hours: hours,
    seasonMode: this.seasonMode
  };

  axios.post('https://thierry-paradis-medecin-743b8e1c5de0.herokuapp.com/admin/open-days', payload)
 // axios.post('http://localhost:3000/admin/open-days', payload)
    .then(response => {
      alert(response.data.message || 'Modifications enregistrées avec succès.');
      this.fetchOpenDays(); // Recharger les jours ouverts pour mettre à jour l'affichage
    })
    .catch(error => {
      console.error('Erreur lors de la sauvegarde des jours ouverts:', error);
      alert('Erreur lors de la sauvegarde des jours ouverts');
    });
}
,

    convertDayToEnglish(day) {
      const daysTranslation = {
        Lundi: 'Monday',
        Mardi: 'Tuesday',
        Mercredi: 'Wednesday',
        Jeudi: 'Thursday',
        Vendredi: 'Friday',
        Samedi: 'Saturday',
        Dimanche: 'Sunday',
      };
      return daysTranslation[day] || day; // Retourner le jour en anglais ou le jour d'origine
    },
  },
  mounted() {
    // Mettre à jour l'heure chaque seconde
    setInterval(() => {
      this.currentTime = new Date().toLocaleTimeString();
    }, 1000);
    this.fetchOpenDays();
    this.fetchClosedDates();
  },
};
</script>



<style scoped>
/* Ajoutez vos styles ici */
</style>
